import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listProducts } from "@/lib/adapters/products";
import { useCart } from "@/contexts/CartContext";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { ArrowRight, ShoppingCart, CheckCircle2, Loader2, Minus, Plus } from "lucide-react";

export default function BulkBuy() {
  const [items, setItems] = useState([]);
  const { openQuote } = useQuoteDrawer();

  useEffect(() => {
    listProducts({ type: "bulk", limit: 10 })
      .then(({ items: rows }) => setItems(rows))
      .catch(() => {});
  }, []);

  return (
    <section className="dyntra-container mt-10">
      <div className="flex items-end justify-between">
        <div>
          <h2 data-testid="bulk-heading" className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Bulk Buy &amp; Save</h2>
        </div>
        <Link to="/shop?type=bulk" data-testid="bulk-view-all" className="text-sm font-semibold text-[#4B2AAD] hover:underline">View All →</Link>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {items.slice(0, 5).map((p) => (
          <BulkCard key={p.id} product={p} />
        ))}

        {/* Custom quote card */}
        <div className="rounded-2xl bg-[#4B2AAD] text-white p-5 flex flex-col justify-between">
          <div>
            <h3 className="font-display text-base font-bold">Need a custom quote?</h3>
            <p className="text-sm text-white/85 mt-1">Get best pricing for bulk requirements</p>
          </div>
          <button
            type="button"
            data-testid="bulk-quote-cta"
            onClick={() => openQuote({ category: "Consumables" })}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-[#4B2AAD] px-4 py-2 text-sm font-bold hover:bg-white/90 transition-colors"
          >
            Request Quote <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}

// Per-card sub-component so each tile owns its own picker + pending-qty
// state independently. Flow mirrors ProductCard's, with one bulk-specific
// twist: `pendingQty` counts *packs*, not individual items. Each pack is
// worth `pack_size` units, so a "Pack of 5" at pendingQty=2 commits 10
// items to the cart. Inventory cap is applied against the underlying unit
// stock so we never request more units than the vendor has on hand.
function BulkCard({ product }) {
  const p = product;
  const { addToCart } = useCart();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingQty, setPendingQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [flash, setFlash] = useState(false);

  const packSize = Math.max(1, Number(p?.pack_size) || 1);
  const inStock = p.in_stock !== false;
  const stockCap = Number.isFinite(Number(p.stock)) && Number(p.stock) > 0
    ? Number(p.stock)
    : Infinity;
  // Max number of PACKS the shopper can dial up given the current unit
  // stock. Falls back to Infinity when we don't have a numeric stock.
  const maxPacks = Number.isFinite(stockCap)
    ? Math.max(1, Math.floor(stockCap / packSize))
    : Infinity;

  const handleAdd = async () => {
    if (adding || !inStock) return;
    // Compact click adds exactly one pack (`pack_size` units) and *opens*
    // the picker so the shopper can dial up more packs. Picker click then
    // commits `pendingQty * pack_size` units and *closes* the picker so
    // the CTA snaps back to compact, confirming the successful commit.
    const wasPickerOpen = pickerOpen;
    const packs = wasPickerOpen ? Math.min(pendingQty, maxPacks) : 1;
    const unitsToAdd = packs * packSize;
    setAdding(true);
    try {
      await addToCart(p.id, unitsToAdd);
      setPickerOpen(!wasPickerOpen);
      setPendingQty(1);
      setFlash(true);
      setTimeout(() => setFlash(false), 2500);
    } finally {
      setAdding(false);
    }
  };

  const handleMinus = () =>
    setPendingQty((n) => Math.max(1, n - 1));
  const handlePlus = () =>
    setPendingQty((n) => Math.min(maxPacks, n + 1));

  return (
    <div data-testid={`bulk-card-${p.id}`} className="dyntra-card p-3 flex flex-col relative overflow-hidden">
      {flash && (
        <div className="absolute top-0 inset-x-0 z-20 bg-[#F3EFFC] text-[#4B2AAD] text-[11px] font-semibold px-3 py-1.5 flex items-center gap-1.5 border-b border-[#4B2AAD]/15">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Added to cart successfully!
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-display text-sm font-bold text-gray-900">Pack of {p.pack_size}</p>
          <p className="text-[11px] text-emerald-600 font-semibold">Extra {p.extra_off}% Off</p>
        </div>
      </div>
      <Link to={`/product/${p.id}`} className="block product-img-wrap aspect-square bg-[#F8F9FA] rounded-xl overflow-hidden mb-3">
        <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
      </Link>
      <div className="mt-auto">
        <p className="text-xs text-gray-500 line-clamp-1">{p.name}</p>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-base font-bold text-gray-900">₹{p.price.toLocaleString()}</span>
          <span className="text-[11px] text-gray-400">/pack</span>
        </div>
        <p className="text-[11px] text-emerald-600 font-semibold">Save ₹{p.savings.toLocaleString()}</p>

        {/* CTA styling mirrors ProductCard exactly (rounded-xl, text-sm,
            font-bold, py-2.5, active-scale, h-4 icon) so the bulk section
            visually matches every other product grid on the site. */}
        {pickerOpen ? (
          <div className="mt-2 flex items-center gap-1.5">
            <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden bg-white shrink-0">
              <button
                type="button"
                data-testid={`bulk-qty-minus-${p.id}`}
                onClick={handleMinus}
                className="px-1.5 py-1 hover:bg-gray-50"
                aria-label="Decrease packs"
              ><Minus className="h-3 w-3" /></button>
              <span data-testid={`bulk-qty-${p.id}`} className="w-5 text-center text-xs font-bold">{pendingQty}</span>
              <button
                type="button"
                data-testid={`bulk-qty-plus-${p.id}`}
                onClick={handlePlus}
                disabled={pendingQty >= maxPacks}
                className="px-1.5 py-1 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Increase packs"
              ><Plus className="h-3 w-3" /></button>
            </div>
            <button
              type="button"
              data-testid={`bulk-add-${p.id}`}
              onClick={handleAdd}
              disabled={adding || !inStock}
              aria-label={`Add ${pendingQty * packSize} to cart`}
              title={`Add ${pendingQty * packSize} to cart`}
              className="flex-1 min-w-0 inline-flex items-center justify-center rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2.5 transition-all active:scale-[0.98]"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`bulk-add-${p.id}`}
            onClick={handleAdd}
            disabled={adding || !inStock}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-sm font-bold py-2.5 transition-all active:scale-[0.98] whitespace-nowrap"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingCart className="h-4 w-4" /> {inStock ? "Add to Cart" : "Out of Stock"}</>}
          </button>
        )}
      </div>
    </div>
  );
}
